import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { 
  LayoutDashboard, 
  Kanban, 
  LogOut, 
  ArrowLeft,
  Shield,
  Menu,
  X,
  Film,
  Receipt,
  Briefcase,
  Cpu,
  FolderGit2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
}

const AdminSidebarContent = ({
  onCloseMobileMenu
}: {
  onCloseMobileMenu: () => void
}) => (
  <>
    <nav className="space-y-2">
      <NavLink
        to="/admin"
        end
        onClick={onCloseMobileMenu}
        className={({ isActive }) =>
          `nav-item w-full ${isActive ? 'active' : ''}`
        }
      >
        <LayoutDashboard className="w-5 h-5" />
        <span>Dashboard</span>
      </NavLink>

      <NavLink
        to="/admin/kanban"
        onClick={onCloseMobileMenu}
        className={({ isActive }) =>
          `nav-item w-full ${isActive ? 'active' : ''}`
        }
      >
        <Kanban className="w-5 h-5" />
        <span>Kanban</span>
      </NavLink>

      <NavLink
        to="/admin/cinema"
        onClick={onCloseMobileMenu}
        className={({ isActive }) =>
          `nav-item w-full ${isActive ? 'active' : ''}`
        }
      >
        <Film className="w-5 h-5" />
        <span>Popcorn</span>
      </NavLink>

      <NavLink
        to="/admin/expenses"
        onClick={onCloseMobileMenu}
        className={({ isActive }) =>
          `nav-item w-full ${isActive ? 'active' : ''}`
        }
      >
        <Receipt className="w-5 h-5" />
        <span>Dépenses</span>
      </NavLink>

      <NavLink
        to="/admin/experiences"
        onClick={onCloseMobileMenu}
        className={({ isActive }) =>
          `nav-item w-full ${isActive ? 'active' : ''}`
        }
      >
        <Briefcase className="w-5 h-5" />
        <span>Expériences</span>
      </NavLink>

      <NavLink
        to="/admin/skills"
        onClick={onCloseMobileMenu}
        className={({ isActive }) =>
          `nav-item w-full ${isActive ? 'active' : ''}`
        }
      >
        <Cpu className="w-5 h-5" />
        <span>Compétences</span>
      </NavLink>

      <NavLink
        to="/admin/projects"
        onClick={onCloseMobileMenu}
        className={({ isActive }) =>
          `nav-item w-full ${isActive ? 'active' : ''}`
        }
      >
        <FolderGit2 className="w-5 h-5" />
        <span>Projets</span>
      </NavLink>
    </nav>

    {/* Info Card */}
    <div className="card card-hover mt-6 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-2 h-2 rounded-full bg-[#108548]" />
        <span className="text-sm font-medium text-[#1d1d1f]">Connecté</span>
      </div>
      <p className="text-xs text-[#5e5e5e]">
        Accès administrateur actif
      </p>
    </div>
  </>
);

export function AdminLayout({ children, title }: AdminLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      {/* Header Admin */}
      <header className="bg-white border-b border-[#e5e5e5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 -ml-2 text-[#5e5e5e] hover:bg-[#f5f5f7] rounded-lg"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>

              <NavLink 
                to="/" 
                className="hidden sm:flex items-center gap-2 text-[#5e5e5e] hover:text-[#1d1d1f] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Retour au portfolio</span>
              </NavLink>
              <div className="hidden sm:block h-4 w-px bg-[#e5e5e5]" />
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#1f75cb]" />
                <span className="font-semibold text-[#1d1d1f]">Admin</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {user && (
                <div className="flex items-center gap-3">
                  {user.picture && (
                    <img 
                      src={user.picture} 
                      alt="Profile" 
                      className="w-8 h-8 rounded-full border border-[#e5e5e5]"
                    />
                  )}
                  <span className="text-sm text-[#5e5e5e] hidden md:block">
                    {user.email}
                  </span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#5e5e5e] hover:text-[#dc3545] hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8 relative">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation - Desktop */}
          <aside className="hidden lg:block w-64 shrink-0">
            <AdminSidebarContent onCloseMobileMenu={() => setIsMobileMenuOpen(false)} />
          </aside>

          {/* Sidebar Navigation - Mobile */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="fixed inset-0 bg-black/20 z-40 lg:hidden"
                />
                <motion.aside
                  initial={{ x: -280 }}
                  animate={{ x: 0 }}
                  exit={{ x: -280 }}
                  className="fixed top-0 left-0 bottom-0 w-72 bg-white z-50 p-4 border-r border-[#e5e5e5] lg:hidden overflow-y-auto"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-2">
                      <Shield className="w-6 h-6 text-[#1f75cb]" />
                      <span className="font-bold text-lg text-[#1d1d1f]">Admin</span>
                    </div>
                    <button
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2 -mr-2 text-[#5e5e5e] hover:bg-[#f5f5f7] rounded-lg"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <AdminSidebarContent onCloseMobileMenu={() => setIsMobileMenuOpen(false)} />

                  <div className="mt-8 pt-8 border-t border-[#e5e5e5]">
                    <NavLink
                      to="/"
                      className="flex items-center gap-2 text-[#5e5e5e] hover:text-[#1d1d1f] transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="text-sm">Retour au portfolio</span>
                    </NavLink>
                  </div>
                </motion.aside>
              </>
            )}
          </AnimatePresence>

          {/* Main Content */}
          <main className="flex-1 w-full min-w-0">
            <h1 className="text-2xl font-semibold text-[#1d1d1f] mb-6">{title}</h1>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
